import { useLocation, Route, Redirect } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { ROLE } from '@constants/user';
import { RoutePath } from '@constants/routes';

const ADVANCED_FEATURE_ROUTES = [
    RoutePath.SurveysTemplates,
    RoutePath.SurveysResponses,
    RoutePath.SurveysAnalyticsConversion,
    RoutePath.SurveysAnalyticsResponses,
    RoutePath.CampaignsOneTime,
    RoutePath.CampaignsAutomated,
    RoutePath.Coupons,
];

const BILLING_EXPIRED_ROUTES = [
    RoutePath.AccountSettingsGeneral,
    RoutePath.AccountSettingsCompanies,
    RoutePath.AccountSettingsBrands,
    RoutePath.AccountSettingsProfile,
    RoutePath.AccountSettingsUsers,
    RoutePath.AccountSettingsChannels,
];

export const PrivateRoute = ({ component: Component, path, ...rest }) => {
    const { pathname } = useLocation();
    const auth = useSelector(state => state.auth);
    const account = useSelector(state => state.account.account);

    if (!auth.user && !auth.loading) {
        return (
            <Route
                {...rest}
                render={props => <Redirect to={{ pathname: RoutePath.Login, state: { from: props.location } }} />}
            />
        );
    }

    if (!account && !path.startsWith(RoutePath.Onboarding)) return null;

    if (auth.user && account) {
        const isSuperAdmin = auth.user?.role === ROLE.SUPER_ADMIN;
        const isAdmin = auth.user?.role === ROLE.ADMIN;
        const isUser = auth.user?.role === ROLE.USER;

        const productsMaxNumberExceeded =
            account &&
            (account?.companiesMaxCount < account?.companies?.length ||
                account?.usersMaxCount < account?.users?.length);
        const billingExpired = account && account?.billingDiffInSeconds < 0;
        const advancedFeaturesLocked =
            !account?.campaignsEnabled && !account?.surveysEnabled && !account?.couponsEnabled;

        // INFO: Redirect to show billing
        if (
            isSuperAdmin &&
            !BILLING_EXPIRED_ROUTES.some(r => r === pathname) &&
            (productsMaxNumberExceeded || billingExpired)
        ) {
            return <Redirect to={RoutePath.AccountSettingsGeneral} />;
        }
        // INFO: Redirect user from settings pages except /profile
        if (isUser && path.startsWith(RoutePath.AccountSettings) && pathname !== RoutePath.AccountSettingsProfile) {
            return <Redirect to={RoutePath.AccountSettingsProfile} />;
        }
        // INFO: Redirect admin from /general and /integrations
        if (
            !isSuperAdmin &&
            isAdmin &&
            (pathname === RoutePath.AccountSettingsGeneral ||
                pathname === RoutePath.AccountSettingsIntegrations ||
                pathname === RoutePath.AccountSettingsBrands ||
                pathname === RoutePath.AccountSettingsChannels)
        ) {
            return <Redirect to={RoutePath.AccountSettingsCompanies} />;
        }
        // INFO: Redirect everyone to the onboarding if it isn't completed
        if (!account.onboardingCompleted && !path.startsWith(RoutePath.Onboarding)) {
            return <Redirect to={RoutePath.Onboarding} />;
        }
        // INFO: Redirect everyone from the onboarding if it's completed
        if (account.onboardingCompleted && path.startsWith(RoutePath.Onboarding)) {
            return <Redirect to={RoutePath.Home} />;
        }
        // INFO: Redirect everyone from the Surveys + Campaigns + Conversions + NPS and Surveys in Analytics if upgrade required
        if (advancedFeaturesLocked && ADVANCED_FEATURE_ROUTES.some(r => pathname === r)) {
            return <Redirect to={RoutePath.Home} />;
        }
    }

    return <Route {...rest} render={props => <Component {...props} />} />;
};

export default PrivateRoute;
